import React, { useState, useEffect } from 'react';
import {
  UserPlus,
  UserCheck,
  Building,
  Mail,
  Phone,
  MapPin,
  Trash2,
  Edit2,
  Save,
  Grid,
  Search,
  Check,
  FileSpreadsheet,
  FolderSync,
  RefreshCw,
  Send,
  Megaphone
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { ClientAccount, DiscountRule } from '../types';
import { getBestDiscountRule, describeDiscountRule } from '../utils/discount';
import DiscountRulesModal from './DiscountRulesModal';
import PromoCampaignModal, { PromoCampaignPayload, PromoSendResult } from './PromoCampaignModal';

import { getPermissions } from '../utils/permissions';
import { getStoredToken } from '../utils/authApi';
import { COUNTRY_CODES as WORLD_COUNTRY_DIAL_CODES, POPULAR_COUNTRIES as POPULAR_COUNTRIES_LIST } from '../utils/countryCodes';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

function detectCountryCode(): string {
  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && WORLD_COUNTRY_DIAL_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached && WORLD_COUNTRY_DIAL_CODES[cached]) return cached;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Tokyo':'JP','Asia/Seoul':'KR',
    'Asia/Shanghai':'CN','Asia/Hong_Kong':'HK','Asia/Taipei':'TW',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Paris':'FR','Europe/Berlin':'DE',
    'Europe/Amsterdam':'NL','Europe/Madrid':'ES','Europe/Rome':'IT','Europe/Zurich':'CH',
    'America/New_York':'US','America/Chicago':'US','America/Los_Angeles':'US',
    'America/Toronto':'CA','America/Sao_Paulo':'BR','America/Mexico_City':'MX',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE','Africa/Johannesburg':'ZA',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && WORLD_COUNTRY_DIAL_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (WORLD_COUNTRY_DIAL_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(WORLD_COUNTRY_DIAL_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length >= fallbackDialCode.length + 5) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

// Validates what was typed in the box and guarantees clean +country+number
export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  // Must have at least 4 digits in the box, and 7 to 15 total international digits
  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

function CountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allCodes = Object.keys(WORLD_COUNTRY_DIAL_CODES);
  const filtered = search
    ? allCodes.filter(c => c.toLowerCase().includes(search.toLowerCase()) || WORLD_COUNTRY_DIAL_CODES[c]?.includes(search.replace('+','')))
    : [...POPULAR_COUNTRIES_LIST, ...allCodes.filter(c => !POPULAR_COUNTRIES_LIST.includes(c))];

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="h-9 flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-l-lg px-2.5 text-xs font-bold text-slate-700 cursor-pointer min-w-[66px] whitespace-nowrap hover:bg-slate-100 transition-colors"
      >
        +{WORLD_COUNTRY_DIAL_CODES[value] || '91'} <span className="text-[9px] opacity-60">▾</span>
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute top-full left-0 z-50 bg-white border border-slate-200 rounded-lg shadow-xl w-48 max-h-60 overflow-hidden flex flex-col mt-1"
        >
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-slate-200 rounded px-2 py-1 text-xs outline-none"
            />
          </div>
          <div className="overflow-y-auto flex-1 accounts-scroll">
            {filtered.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                className={`flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs cursor-pointer hover:bg-slate-50 ${c === value ? 'bg-blue-50 font-bold text-blue-600' : 'text-slate-700'}`}
              >
                <span className="font-bold min-w-[36px]">+{WORLD_COUNTRY_DIAL_CODES[c]}</span>
                <span className="truncate">{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PhoneInput({ value, onChange, defaultCountry }: { value: string; onChange: (v: string) => void; defaultCountry?: string }) {
  const parsed = parsePhoneValue(value, defaultCountry);
  const [country, setCountry] = React.useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    if (defaultCountry && WORLD_COUNTRY_DIAL_CODES[defaultCountry]) return defaultCountry;
    return detectCountryCode();
  });
  const [number, setNumber] = React.useState(() => parsed.nationalNumber);
  const lastEmitted = React.useRef(value);
  const isSyncing = React.useRef(false);

  React.useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    isSyncing.current = true;
    if (!value) {
      setNumber('');
    } else {
      const p = parsePhoneValue(value, defaultCountry);
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = WORLD_COUNTRY_DIAL_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    let cleanNum = raw.replace(/\D/g, '');
    const dialCode = (WORLD_COUNTRY_DIAL_CODES[country] || '91').replace(/\D/g, '');

    // Strip dial code if pasted with '+' (e.g. "+91 9108582545")
    // or if pasted with full dial code prefix exceeding 10 digits (e.g. "919108582545")
    if (raw.includes('+') && cleanNum.startsWith(dialCode)) {
      cleanNum = cleanNum.slice(dialCode.length);
    } else if (cleanNum.startsWith(dialCode) && cleanNum.length > 10) {
      cleanNum = cleanNum.slice(dialCode.length);
    }

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div className="flex w-full items-center">
      <CountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={(e) => handleNumberChange(e.target.value)}
        placeholder="Phone number"
        className="w-full h-9 px-3 text-xs font-semibold text-slate-700 bg-white border border-l-0 border-slate-200 rounded-r-lg focus:outline-none"
      />
    </div>
  );
}

interface OutletOption {
  id: number;
  name: string;
}

interface AccountsViewProps {
  userRole?: string;
  authUserPerms?: Record<string, boolean>;
  moduleStatuses?: Partial<Record<import('../utils/permissions').ModuleId, import('../utils/permissions').ModuleStatus>>;
  authUserPlan?: string | null;
  authUserPlanTier?: string | null;
  authUserPlanStatus?: string | null;
  accounts: ClientAccount[];
  onAddAccount: (account: Omit<ClientAccount, 'id' | 'createdAt'>) => void;
  onUpdateAccount: (account: ClientAccount) => void;
  onDeleteAccount: (id: string) => void;
  onUseAccountInInvoice: (account: ClientAccount) => void;
  googleToken: string | null;
  onBackupAccountsToDrive?: (accounts: ClientAccount[]) => void;
  discountRules: DiscountRule[];
  onAddDiscountRule: (rule: Omit<DiscountRule, 'id' | 'createdAt'>) => void;
  onUpdateDiscountRule: (rule: DiscountRule) => void;
  onDeleteDiscountRule: (id: string) => void;
  onSendPromoCampaign: (payload: PromoCampaignPayload) => Promise<PromoSendResult>;
}

export default function AccountsView({
  userRole,
  authUserPerms,
  moduleStatuses,
  authUserPlan,
  authUserPlanTier,
  authUserPlanStatus,
  accounts,
  onAddAccount,
  onUpdateAccount,
  onDeleteAccount,
  onUseAccountInInvoice,
  googleToken,
  onBackupAccountsToDrive,
  discountRules,
  onAddDiscountRule,
  onUpdateDiscountRule,
  onDeleteDiscountRule,
  onSendPromoCampaign
}: AccountsViewProps) {
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [outlets, setOutlets] = useState<OutletOption[]>([]);
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    const token = getStoredToken();
    if (!token) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setOutlets(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch(`${BACKEND}/auth/company-profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data?.name) setCompanyName(data.name);
      })
      .catch(() => {});
  }, []);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Search & editing states
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [membershipLabel, setMembershipLabel] = useState('');
  const [exemptFromAutoDiscount, setExemptFromAutoDiscount] = useState(false);
  const [outletId, setOutletId] = useState<number | null>(null);

  // Editing form states
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editMembershipLabel, setEditMembershipLabel] = useState('');
  const [editExemptFromAutoDiscount, setEditExemptFromAutoDiscount] = useState(false);
  const [editMarketingOptOut, setEditMarketingOptOut] = useState(false);
  const [editOutletId, setEditOutletId] = useState<number | null>(null);

  // Local sync/backup state
  const [isUploadingToDrive, setIsUploadingToDrive] = useState(false);
  const perms = getPermissions(userRole, authUserPerms, authUserPlan, authUserPlanTier, authUserPlanStatus, moduleStatuses ?? null);

  if (!perms.canAccessAccounts) {
    return (
      <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af', fontSize: 14, fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        You don't have permission to view client accounts.
      </div>
    );
  }

  // Excel Export for Client Accounts Directory
  const handleExportAccountsToExcel = () => {
    if (accounts.length === 0) {
      alert("No client accounts available to export.");
      return;
    }
    try {
      const data = accounts.map((a, idx) => ({
        "Serial Number": idx + 1,
        "Client Name": a.name,
        "Corporate Company": a.company || 'Private Individual / General Contractor',
        "Email Address ID": a.email,
        "Phone Contact": a.phone || 'N/A',
        "Physical / Mailing Address": a.address || 'N/A',
        "Registration Date": new Date(a.createdAt.endsWith('Z') || a.createdAt.includes('+') ? a.createdAt : a.createdAt + 'Z').toLocaleDateString()
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Client Registry");

      // Auto size column widths
      const maxProps = [
        { wch: 15 },
        { wch: 22 },
        { wch: 25 },
        { wch: 25 },
        { wch: 18 },
        { wch: 45 },
        { wch: 15 }
      ];
      ws['!cols'] = maxProps;

      const prefix = companyName ? companyName.replace(/\s+/g, '_') : 'Accounts';
      XLSX.writeFile(wb, `${prefix}_Directory_${Date.now().toString().slice(-6)}.xlsx`);
    } catch (err) {
      console.error(err);
      alert("Failed compiling accounts spreadsheet.");
    }
  };

  const handleBackupToDrive = async () => {
    if (accounts.length === 0) {
      alert("No client accounts available to backup. Create one first.");
      return;
    }
    if (!onBackupAccountsToDrive) return;
    setIsUploadingToDrive(true);
    try {
      await onBackupAccountsToDrive(accounts);
    } catch (err) {
      console.error(err);
    } finally {
      setIsUploadingToDrive(false);
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return alert("Client name is required");
    onAddAccount({
      name, email, company,
      phone: getCleanE164Phone(phone),
      address,
      visitCount: 0,
      totalSpend: 0,
      membershipLabel: membershipLabel.trim() || undefined,
      exemptFromAutoDiscount,
      outletId,
    });

    // reset
    setName('');
    setEmail('');
    setCompany('');
    setPhone('');
    setAddress('');
    setMembershipLabel('');
    setExemptFromAutoDiscount(false);
    setOutletId(null);
  };

  const handleStartEdit = (a: ClientAccount) => {
    setEditingId(a.id);
    setEditName(a.name);
    setEditEmail(a.email);
    setEditCompany(a.company);
    setEditPhone(a.phone);
    setEditAddress(a.address);
    setEditMembershipLabel(a.membershipLabel || '');
    setEditExemptFromAutoDiscount(a.exemptFromAutoDiscount || false);
    setEditMarketingOptOut(a.marketingOptOut || false);
    setEditOutletId(a.outletId ?? null);
  };

  const handleSaveEdit = (id: string) => {
    if (!editName.trim()) return alert("Client name cannot be empty");
    const existing = accounts.find(a => a.id === id);
    onUpdateAccount({
      id,
      name: editName,
      email: editEmail,
      company: editCompany,
      phone: getCleanE164Phone(editPhone),
      address: editAddress,
      createdAt: existing?.createdAt || new Date().toISOString(),
      visitCount: existing?.visitCount || 0,
      totalSpend: existing?.totalSpend || 0,
      membershipLabel: editMembershipLabel.trim() || undefined,
      exemptFromAutoDiscount: editExemptFromAutoDiscount,
      marketingOptOut: editMarketingOptOut,
      outletId: editOutletId,
    });
    setEditingId(null);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected client${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    selectedIds.forEach(id => onDeleteAccount(id));
    setSelectedIds(new Set());
  };

  const showCheckboxCol = perms.canSendPromo || perms.canDeleteAccounts;

  const filteredAccounts = accounts.filter(
    (a) =>
      a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.phone && a.phone.replace(/\D/g, '').includes(searchTerm.replace(/\D/g, '')))
  );

  return (
    <>
    <style>{`
      .accounts-scroll {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .accounts-scroll::-webkit-scrollbar {
        width: 0px;
        height: 0px;
        display: none;
      }
    `}</style>
    <div 
      className="flex-1 overflow-y-auto bg-slate-50/50 p-6 space-y-8 select-none font-sans accounts-scroll"
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      
      {/* HEADER SECTION */}
      <div className="border-b border-slate-200 pb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-blue-600" />
            <span>Institutional Clients Directory & Accounts</span>
          </h1>
          <p className="text-xs text-slate-450 mt-1 leading-normal">
            Manage corporate billing accounts. Selected profiles can instantly replace placeholder billing text elements inside the vector creator!
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Add Client */}
          {perms.canEditAccounts && (
            <button
              onClick={() => setShowAddModal(true)}
              className="h-9 flex items-center gap-1.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-10 text-xs font-bold uppercase cursor-pointer transition-all shadow-xs"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add Client</span>
            </button>
          )}
          {/* Manage Discount Rules */}
          {perms.canEditAccounts && (
            <button
              onClick={() => setShowRulesModal(true)}
              className="h-9 flex items-center gap-1.5 px-3.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-10 text-xs font-bold uppercase cursor-pointer transition-all shadow-xs"
              title="Set up automatic loyalty discounts based on visit count or total spend"
            >
              <span>🏷️</span>
              <span>Manage Discount Rules{discountRules.length > 0 ? ` (${discountRules.length})` : ''}</span>
            </button>
          )}
          {/* Send Promo Campaign */}
          {perms.canSendPromo && (
            <button
              onClick={() => setShowPromoModal(true)}
              disabled={selectedIds.size === 0}
              className="h-9 flex items-center gap-1.5 px-3.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-10 text-xs font-bold uppercase cursor-pointer transition-all shadow-xs"
              title={selectedIds.size === 0 ? 'Select clients below first' : 'Send a promo video/post to selected clients'}
            >
              <Megaphone className="w-4 h-4" />
              <span>Send Promo{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}</span>
            </button>
          )}

          {/* Export to Excel */}
          {perms.canExportExcel && (
            <button
              onClick={handleExportAccountsToExcel}
              className="h-9 flex items-center gap-1.5 px-3.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-10 text-xs font-bold uppercase cursor-pointer transition-all shadow-xs"
              title="Download full client accounts spreadsheet backup"
            >
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              <span>Export directory (.xlsx)</span>
            </button>
          )}
           {/* Backup to Drive */}
          {perms.canExportExcel && googleToken ? (
            <button
              onClick={handleBackupToDrive}
              disabled={isUploadingToDrive}
              className="flex items-center gap-1.5 px-3 py-2 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-lg text-xs font-bold uppercase cursor-pointer transition-all shadow-xs disabled:opacity-40"
              title="Backup current accounts list to your Google Drive folder"
            >
              {isUploadingToDrive ? (
                <RefreshCw className="w-4 h-4 animate-spin text-sky-650" />
              ) : (
                <FolderSync className="w-4 h-4 text-sky-650" />
              )}
              <span>{isUploadingToDrive ? "Backing up..." : "Backup to Drive"}</span>
            </button>
          ) : perms.canExportExcel ? (
            <span className="text-[10px] tracking-wide text-slate-405 font-mono italic bg-slate-100/80 border border-slate-200 py-2 px-3 rounded-lg" title="Link your Google Drive on the Home Tab first">
              Link Drive to Sync
            </span>
          ) : null}
        </div>
      </div>

      

        <div className="space-y-4">
          
          {/* SEARCH BAR */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Filter clients by name, corporate company, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 pl-9 pr-4 bg-white border border-slate-200 rounded-10 text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs transition-all box-border"
              />
            </div>
            {showCheckboxCol && filteredAccounts.length > 0 && (
              <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 whitespace-nowrap cursor-pointer bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
                <input
                  type="checkbox"
                  checked={filteredAccounts.every(a => selectedIds.has(a.id))}
                  onChange={(e) => {
                    setSelectedIds(prev => {
                      const next = new Set(prev);
                      filteredAccounts.forEach(a => e.target.checked ? next.add(a.id) : next.delete(a.id));
                      return next;
                    });
                  }}
                  className="w-3.5 h-3.5"
                />
                Select all ({filteredAccounts.length})
              </label>
            )}
            {perms.canDeleteAccounts && selectedIds.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 text-[11px] font-bold text-red-600 whitespace-nowrap cursor-pointer bg-red-50 border border-red-200 hover:bg-red-100 rounded-xl px-3 py-2 shadow-sm"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete ({selectedIds.size})
              </button>
            )}
          </div>

          {/* ACCOUNTS TABLE (EXCEL-STYLE GRID) */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto accounts-scroll">
              <table 
                className="w-full text-xs border-collapse border border-slate-200"
                style={{ 
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.01em',
                }}
              >
                <thead>
                  <tr 
                    className="bg-slate-100/80 border-b border-slate-200 text-slate-800 font-extrabold text-xs uppercase select-none"
                    style={{ letterSpacing: '0.04em' }}
                  >
                    {showCheckboxCol && <th className="p-3 w-10 text-center border-r border-slate-200"></th>}
                    <th className="p-3 text-left border-r border-slate-200">Customer Name</th>
                    <th className="p-3 text-left border-r border-slate-200">Branch / Outlet</th>
                    <th className="p-3 text-left border-r border-slate-200">Email Address</th>
                    <th className="p-3 text-left border-r border-slate-200">Phone Number</th>
                    <th className="p-3 text-left border-r border-slate-200">Location / Address</th>
                    <th className="p-3 text-right border-r border-slate-200">Visits</th>
                    <th className="p-3 text-right border-r border-slate-200">Total Spent</th>
                    <th className="p-3 text-left border-r border-slate-200">Loyalty / Discount</th>
                    <th className="p-3 text-center w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredAccounts.map((a) => {
                    const isEditing = editingId === a.id;

                    if (isEditing) {
                      return (
                        <tr key={a.id} className="bg-slate-50/90 border-b-2 border-blue-500 shadow-inner">
                          <td colSpan={showCheckboxCol ? 10 : 9} className="p-5">
                            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                                  <span className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">
                                    Editing Client Profile: <span className="text-blue-600">{a.name}</span>
                                  </span>
                                </div>
                                <span className="text-[11px] text-slate-400 font-mono">ID: {a.id}</span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
                                <div>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Client Name *</label>
                                  <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                                    className="w-full h-9 px-3 bg-slate-50 border border-slate-200 text-xs font-semibold rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Company</label>
                                  <input type="text" value={editCompany} onChange={(e) => setEditCompany(e.target.value)}
                                    placeholder="Company name"
                                    className="w-full h-9 px-3 bg-slate-50 border border-slate-200 text-xs font-semibold rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Email</label>
                                  <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)}
                                    placeholder="client@email.com"
                                    className="w-full h-9 px-3 bg-slate-50 border border-slate-200 text-xs font-semibold rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white font-mono" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Phone</label>
                                  <PhoneInput value={editPhone} onChange={setEditPhone} />
                                </div>

                                <div className="sm:col-span-2">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Billing / Shipping Address</label>
                                  <input type="text" value={editAddress} onChange={(e) => setEditAddress(e.target.value)}
                                    placeholder="Full address"
                                    className="w-full h-9 px-3 bg-slate-50 border border-slate-200 text-xs font-semibold rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Membership Label</label>
                                  <input type="text" value={editMembershipLabel} onChange={(e) => setEditMembershipLabel(e.target.value)}
                                    placeholder="e.g. VIP, Gold, Wholesale"
                                    className="w-full h-9 px-3 bg-slate-50 border border-slate-200 text-xs font-semibold rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white" />
                                </div>
                                <div>
                                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Applies To Outlet</label>
                                  <select value={editOutletId === null ? '' : String(editOutletId)}
                                    onChange={(e) => setEditOutletId(e.target.value === '' ? null : Number(e.target.value))}
                                    className="w-full h-9 px-3 bg-slate-50 border border-slate-200 text-xs font-semibold rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white cursor-pointer">
                                    <option value="">All outlets</option>
                                    {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                                  </select>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                                <div className="flex items-center gap-4">
                                  <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg">
                                    <input type="checkbox" checked={editExemptFromAutoDiscount}
                                      onChange={(e) => setEditExemptFromAutoDiscount(e.target.checked)} className="w-4 h-4 accent-blue-600 rounded" />
                                    <span className="text-[11px] font-bold text-slate-700">Exempt from auto-discounts</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg">
                                    <input type="checkbox" checked={editMarketingOptOut}
                                      onChange={(e) => setEditMarketingOptOut(e.target.checked)} className="w-4 h-4 accent-blue-600 rounded" />
                                    <span className="text-[11px] font-bold text-slate-700">Opted out of marketing</span>
                                  </label>
                                </div>

                                <div className="flex items-center gap-2">
                                  <button onClick={() => setEditingId(null)}
                                    className="h-9 px-4 border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-10 text-xs font-bold cursor-pointer transition-all">
                                    Cancel
                                  </button>
                                  <button onClick={() => handleSaveEdit(a.id)}
                                    className="h-9 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-10 text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-all shadow-xs">
                                    <Save className="w-4 h-4" />
                                    <span>Save Changes</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const activeRule = getBestDiscountRule(a, discountRules, 100);

                    return (
                      <tr key={a.id} className={`hover:bg-slate-50/80 transition-colors font-sans ${selectedIds.has(a.id) ? 'bg-blue-50/40' : ''}`}>
                        {showCheckboxCol && (
                          <td className="p-3 text-center border-r border-slate-200">
                            <input type="checkbox" checked={selectedIds.has(a.id)} onChange={() => toggleSelect(a.id)} className="w-4 h-4 accent-blue-600 rounded cursor-pointer" />
                          </td>
                        )}
                        <td className="p-3 border-r border-slate-200">
                          <div className="font-bold text-slate-900 text-xs whitespace-nowrap">{a.name}</div>
                          {a.company && (
                            <div className="text-[11px] text-blue-600 font-semibold flex items-center gap-1 mt-0.5 whitespace-nowrap">
                              <Building className="w-3 h-3 text-blue-500" />
                              {a.company}
                            </div>
                          )}
                          {a.membershipLabel && (
                            <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                              {a.membershipLabel}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-slate-600 font-medium border-r border-slate-200 whitespace-nowrap">
                          {a.outletId ? (
                            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-semibold text-[11px]">
                              {outlets.find(o => o.id === a.outletId)?.name || `#${a.outletId}`}
                            </span>
                          ) : (
                            <span className="text-slate-400">All outlets</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-slate-600 font-medium border-r border-slate-200 whitespace-nowrap">{a.email || '—'}</td>
                        <td className="p-3 text-xs font-semibold text-slate-700 border-r border-slate-200 whitespace-nowrap tabular-nums">{a.phone || '—'}</td>
                        <td className="p-3 text-xs text-slate-500 border-r border-slate-200 max-w-[200px] truncate" title={a.address}>{a.address || '—'}</td>
                        <td className="p-3 text-xs font-bold text-slate-800 text-right border-r border-slate-200 tabular-nums whitespace-nowrap">{a.visitCount || 0}</td>
                        <td className="p-3 text-xs font-bold text-slate-900 text-right border-r border-slate-200 tabular-nums whitespace-nowrap">
                          {(a.totalSpend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 border-r border-slate-200">
                          {activeRule && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5 whitespace-nowrap">
                              <span>🏷️</span>
                              {describeDiscountRule(activeRule)}
                            </span>
                          )}
                          {a.exemptFromAutoDiscount && (
                            <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5 whitespace-nowrap">
                              Exempt
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {perms.canAccessGenerator && (
                              <button
                                onClick={() => onUseAccountInInvoice(a)}
                                className="w-8 h-8 flex items-center justify-center bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white border border-emerald-200 hover:border-emerald-600 rounded-lg cursor-pointer transition-all shadow-xs"
                                title="Use in Creator"
                              >
                                <Check className="w-4 h-4 stroke-[2.5]" />
                              </button>
                            )}
                            {perms.canEditAccounts && (
                              <button
                                onClick={() => handleStartEdit(a)}
                                className="w-8 h-8 flex items-center justify-center bg-slate-50 hover:bg-blue-600 text-slate-600 hover:text-white border border-slate-200 hover:border-blue-600 rounded-lg cursor-pointer transition-all shadow-xs"
                                title="Edit Profile"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {perms.canDeleteAccounts && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`Delete client profile ${a.name}?`)) onDeleteAccount(a.id);
                                }}
                                className="w-8 h-8 flex items-center justify-center bg-slate-50 hover:bg-rose-600 text-slate-500 hover:text-white border border-slate-200 hover:border-rose-600 rounded-lg cursor-pointer transition-all shadow-xs"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredAccounts.length === 0 && (
                    <tr>
                      <td colSpan={showCheckboxCol ? 10 : 9} className="py-12 text-center text-slate-450">
                        <p className="font-bold text-sm text-slate-700">No client registry matches found</p>
                        <p className="text-xs text-slate-400 mt-1">Enroll your clients first using "+ Add Client" above.</p>
                      </td>
                    </tr>
                  )}
                </tbody>

                {/* ── EXCEL SPREADSHEET FOOTER BAR (SUMMARY ROW) ── */}
                {filteredAccounts.length > 0 && (() => {
                  const totalVisits = filteredAccounts.reduce((acc, a) => acc + (a.visitCount || 0), 0);
                  const totalSpent = filteredAccounts.reduce((acc, a) => acc + (a.totalSpend || 0), 0);
                  const avgSpent = filteredAccounts.length > 0 ? totalSpent / filteredAccounts.length : 0;
                  return (
                    <tfoot 
                      className="bg-slate-100 border-t-2 border-slate-300"
                      style={{ 
                        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      <tr>
                        {showCheckboxCol && <td className="p-3 border-r border-slate-200 text-center text-xs font-bold text-slate-500">∑</td>}
                        <td className="p-3 border-r border-slate-200 text-xs font-extrabold text-slate-800 uppercase tracking-wide">
                          Total: {filteredAccounts.length} Client{filteredAccounts.length !== 1 ? 's' : ''}
                        </td>
                        <td className="p-3 border-r border-slate-200 text-[11px] text-slate-400 font-semibold">—</td>
                        <td className="p-3 border-r border-slate-200 text-[11px] text-slate-400 font-semibold">—</td>
                        <td className="p-3 border-r border-slate-200 text-[11px] text-slate-400 font-semibold">—</td>
                        <td className="p-3 border-r border-slate-200 text-[11px] text-slate-400 font-semibold">—</td>
                        <td className="p-3 border-r border-slate-200 text-right text-xs font-extrabold text-slate-800 font-mono">
                          {totalVisits.toLocaleString()}
                        </td>
                        <td className="p-3 border-r border-slate-200 text-right text-xs font-extrabold text-blue-700 font-mono">
                          {totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 border-r border-slate-200 text-[11px] text-slate-600 font-semibold">
                          Avg: {avgSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 text-center text-[11px] text-slate-400 font-semibold">—</td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          </div>
        </div>

      {showAddModal && perms.canEditAccounts && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-blue-600" />
                <span>Enroll New Client Profile</span>
              </h2>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer rounded-lg hover:bg-slate-100 transition-all">
                ✕
              </button>
            </div>

            <form onSubmit={(e) => { handleCreate(e); setShowAddModal(false); }} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Contact Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Martha Wayne"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full h-10 px-3.5 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Corporate / Company</label>
                  <input
                    type="text"
                    placeholder="e.g. Wayne Enterprises"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full h-10 px-3.5 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Email Address</label>
                  <input
                    type="email"
                    placeholder="martha@wayne.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-10 px-3.5 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all font-mono"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Phone Number</label>
                  <PhoneInput value={phone} onChange={setPhone} />
                </div>

                <div className="sm:col-span-2">
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Full Billing / Shipping Address</label>
                  <input
                    type="text"
                    placeholder="1007 Mountain Drive, Gotham City, NJ"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full h-10 px-3.5 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Membership Label</label>
                  <input
                    type="text"
                    placeholder="e.g. Gold Member, VIP, Wholesale"
                    value={membershipLabel}
                    onChange={(e) => setMembershipLabel(e.target.value)}
                    className="w-full h-10 px-3.5 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-1">Applies To Outlet</label>
                  <select
                    value={outletId === null ? '' : String(outletId)}
                    onChange={(e) => setOutletId(e.target.value === '' ? null : Number(e.target.value))}
                    className="w-full h-10 px-3.5 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all cursor-pointer"
                  >
                    <option value="">All outlets (global)</option>
                    {outlets.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exemptFromAutoDiscount}
                    onChange={(e) => setExemptFromAutoDiscount(e.target.checked)}
                    className="w-4 h-4 accent-blue-600 rounded"
                  />
                  <span className="text-xs font-bold text-slate-700">Exempt from auto-discounts</span>
                </label>
                <p className="text-[10.5px] text-slate-400 pl-6 leading-relaxed">
                  Discounts apply automatically based on visits and spend under "Manage Discount Rules". Check this if this client should never receive automatic discounts.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="h-10 px-4 border border-slate-300 hover:bg-slate-100 text-slate-600 rounded-10 text-xs font-bold transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-10 text-xs font-bold transition-all shadow-xs cursor-pointer"
                >
                  Add Client Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRulesModal && (
        <DiscountRulesModal
          rules={discountRules}
          onAddRule={onAddDiscountRule}
          onUpdateRule={onUpdateDiscountRule}
          onDeleteRule={onDeleteDiscountRule}
          onClose={() => setShowRulesModal(false)}
        />
      )}

      {showPromoModal && (
        <PromoCampaignModal
          companyName={companyName}
          recipients={accounts.filter(a => selectedIds.has(a.id))}
          onRemoveRecipient={(id) => setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          })}
          onSend={onSendPromoCampaign}
          onClose={() => { setShowPromoModal(false); setSelectedIds(new Set()); }}
        />
      )}

    </div>
  </>
);
}
